/* eslint-disable no-loop-func */
import dayjs from "dayjs";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  setDoc,
  where,
  updateDoc,
  orderBy,
} from "firebase/firestore";
import { db } from "../../../connection";
import { getGroups } from "../../clubAdmin/groups";
import { _sendNotification } from "../content";
import { continents, countries } from "countries-list";
import { isToday } from "../../../../../utils/helper";
import logger from "../../../../../utils/logger";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Returns true if the trigger (stored as UTC start-of-day) is "today" for instant send.
 * Compares trigger's UTC calendar date to today's date in the user's timezone,
 * so we don't send early when it's still the previous day for the user (e.g. 9pm Atlanta = already Feb 25 UTC).
 * @param {number} triggerTimestamp - ms (13 digits), UTC start-of-day
 * @param {string} timezoneName - IANA timezone (e.g. "America/New_York")
 */
function isTodayInTimezone(triggerTimestamp, timezoneName) {
  if (!timezoneName || typeof timezoneName !== "string") return false;
  const ts =
    triggerTimestamp.toString().length === 10
      ? triggerTimestamp * 1000
      : triggerTimestamp;
  const triggerDateUtc = dayjs.utc(ts).format("YYYY-MM-DD");
  const todayInUserTz = dayjs().tz(timezoneName).format("YYYY-MM-DD");
  return triggerDateUtc === todayInUserTz;
}

function verifyEventTimestamp(timestamp) {
  if (typeof timestamp !== "number" || isNaN(timestamp)) {
    return {
      isValid: false,
      errorMessage: `Timestamp must be a valid number but got ${timestamp}`,
    };
  }

  const timestampStr = Math.abs(timestamp).toString();
  if (timestampStr.length !== 13) {
    return {
      isValid: false,
      errorMessage: `Timestamp must be in milliseconds format (13 digits) but got ${timestamp}`,
    };
  }

  const date = dayjs.utc(timestamp);

  if (!date.isValid()) {
    return {
      isValid: false,
      errorMessage: `Invalid date timestamp ${timestamp}`,
    };
  }

  // Verify it's already UTC start-of-day
  const startOfDay = date.startOf("day");
  if (timestamp !== startOfDay.valueOf()) {
    return {
      isValid: false,
      errorMessage: `Timestamp must be start of day in UTC (00:00:00.000) but got ${timestamp}`,
    };
  }

  // Timestamp is already UTC start-of-day, use it directly for docId
  return { isValid: true, errorMessage: null, timestamp };
}

/**
 * Fetches events from Firebase based on tab (current/past) and group type
 * @param {string} groupId - The group ID
 * @param {string} tab - "current" or "past"
 * @param {boolean} isParent - Whether this is a parent group
 * @returns {Promise<Array>} Array of events
 */
export const getEventsFromDb = async (groupId, tab, isParent = false) => {
  try {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    const dateTimestamp = parseInt(date.getTime() / 1000);

    const LOOKBACK_DAYS = 30;
    const lookbackTimestamp = dateTimestamp - LOOKBACK_DAYS * 24 * 60 * 60;

    const isCurrentEvent = (event) => {
      const eventStartDate = event?.details?.eventStartDate;
      const showOnHomePageEnd = event?.showOnHomePageEnd;

      if (eventStartDate >= dateTimestamp) {
        return true;
      }

      if (showOnHomePageEnd && showOnHomePageEnd >= dateTimestamp) {
        return true;
      }

      return false; // Both dates have passed
    };

    let allGroupEvents = [];

    const groupEventsCollection = isParent
      ? collection(db, "parentGroups", groupId, "events")
      : collection(db, "groupAdminPost", groupId, "posts");

    if (tab === "current") {
      const futureEventsQuery = query(
        groupEventsCollection,
        where("details.eventStartDate", ">=", dateTimestamp),
        orderBy("details.eventStartDate", "asc"),
      );
      const futureSnapshot = await getDocs(futureEventsQuery);
      allGroupEvents = futureSnapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }));

      // Fetch RECENT past events (within lookback window) that might still have active showOnHomePageEnd
      // Only look back 30 days to avoid fetching all historical events
      const recentPastEventsQuery = query(
        groupEventsCollection,
        where("details.eventStartDate", ">=", lookbackTimestamp),
        where("details.eventStartDate", "<", dateTimestamp),
        orderBy("details.eventStartDate", "desc"),
      );
      const recentPastSnapshot = await getDocs(recentPastEventsQuery);
      const recentPastEvents = recentPastSnapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }));

      // Filter to only include those with showOnHomePageEnd still active
      const activePastEvents = recentPastEvents.filter(
        (event) =>
          event.showOnHomePageEnd && event.showOnHomePageEnd >= dateTimestamp,
      );

      allGroupEvents = [...allGroupEvents, ...activePastEvents];
      allGroupEvents.sort(
        (a, b) => a.details.eventStartDate - b.details.eventStartDate,
      );
    } else {
      // Past tab: fetch events where eventStartDate < today
      const pastEventsQuery = query(
        groupEventsCollection,
        where("details.eventStartDate", "<", dateTimestamp),
        orderBy("details.eventStartDate", "desc"),
      );
      const pastSnapshot = await getDocs(pastEventsQuery);
      allGroupEvents = pastSnapshot.docs
        .map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }))
        .filter((event) => !isCurrentEvent(event)); // Only truly past events
    }

    return allGroupEvents;
  } catch (err) {
    logger.error("Error fetching events:", err);
    throw err; // Re-throw to handle in calling code
  }
};

const processClubAdminNotifications = async (data, ctnId, notificationTimezone = null) => {
  const eventDate = notificationTimezone
    ? dayjs.unix(data.details.eventStartDate)
        .tz(notificationTimezone)
        .startOf("day")
        .set("second", 59)
        .unix()
    : dayjs.unix(data.details.eventStartDate)
        .startOf("day")
        .set("second", 59)
        .unix();

  const matchName = data.matchName?.trim().split("(")[0].trim() || "";

  try {
    const groups = await getGroups();
    let groupIds = [];
    const clubGroup = groups.find((group) => group.isParent);
    const clubGroupId = clubGroup?.id;

    // Process groups based on the data.groups array
    for (let i = 0; i < data.groups.length; i++) {
      const param = data.groups[i];
      if (param === "global") {
        groupIds = [...groups.map((group) => group.id)];
        break; // If global is specified, use all groups
      }

      const filteredGroups = groups.filter((group) => {
        const isMatchingId =
          group.id === param ||
          continents?.[group.supportersRegion] === param ||
          countries?.[group.supportersCountry]?.name === param;

        return isMatchingId && !groupIds.includes(group.id);
      });

      groupIds = [...groupIds, ...filteredGroups.map((group) => group.id)];
    }

    // Send notifications to all relevant groups
    const sendPromises = groupIds.map((gId) =>
      _sendNotification(
        data.title,
        data.type,
        data.volunteerRequests || {},
        data?.ticketId || null,
        eventDate,
        matchName,
        gId,
        clubGroupId,
        ctnId,
        data.showForFans,
      ),
    );

    await Promise.all(sendPromises);
    return true;
  } catch (error) {
    console.error("Error processing group notifications:", error);
    return false;
  }
};

export const updateEventNotifications = async (
  data,
  oldNoti,
  gId,
  ctnId,
  isDeleteOnly = false,
  notificationTimezone = null,
) => {
  try {
    let aList = [];
    let dList = [];
    let updatePromises = [];

    const eventDate = notificationTimezone
      ? dayjs.unix(data.details.eventStartDate)
          .tz(notificationTimezone)
          .startOf("day")
          .set("second", 59)
          .unix()
      : dayjs.unix(data.details.eventStartDate)
          .startOf("day")
          .set("second", 59)
          .unix();

    const notifications = Array.isArray(data?.notifications)
      ? data?.notifications?.filter((item) => typeof item === "number")
      : [];

    const oldNotifications = Array.isArray(oldNoti)
      ? oldNoti.filter((item) => typeof item === "number")
      : [];

    // For delete-only case, set dList to all old notifications
    if (isDeleteOnly) {
      dList = oldNotifications;
      aList = [];
    } else {
      // Find new notifications to add
      aList = notifications.filter((f) => !oldNotifications.includes(f));
      // Find notifications to delete
      dList = oldNotifications.filter((w) => !notifications.includes(w));
    }

    // Only check for updates if not in delete-only mode
    if (!isDeleteOnly) {
      // Check for existing notifications that need updating due to event changes
      const existingNotifications = notifications.filter((f) =>
        oldNotifications.includes(f),
      );
      if (existingNotifications.length > 0) {
        // If we have existing notifications, check if event details have changed
        const eventDetailsChanged = haveEventDetailsChanged(data, oldNoti);
        if (eventDetailsChanged) {
          // Update all existing notifications with new event details
          for (const triggerTimestamp of existingNotifications) {
            const verifiedTimestamp = verifyEventTimestamp(triggerTimestamp);

            let dayTimestamp = null;

            if (verifiedTimestamp.isValid) {
              dayTimestamp = verifiedTimestamp.timestamp;
            } else {
              logger.error(verifiedTimestamp.errorMessage);
              continue;
            }

            if (dayTimestamp) {
              const notificationsCollectionRef = collection(
                db,
                "contentNotifications",
                gId,
                "dates",
                dayTimestamp.toString(),
                "notifications",
              );

              const q = query(
                notificationsCollectionRef,
                where("triggerTime", "==", triggerTimestamp),
                where("eventId", "==", ctnId),
              );

              const querySnapshot = await getDocs(q);
              querySnapshot.forEach((document) => {
                const updatedNotification = {
                  eventName: data.title || "",
                  matchName: data.matchName?.trim().split("(")[0].trim() || "",
                  eventTime: eventDate,
                  eventType: data.type,
                  volunteerOption:
                    Object.keys(data.volunteerRequests || {}).length > 0,
                  showForFans: data.showForFans || false,
                  lastUpdated: Date.now(),
                };
                updatePromises.push(
                  updateDoc(document.ref, updatedNotification),
                );
              });
            }
          }
        }
      }
    }

    const newNotificationPromises = [];
    const todayNotificationPromises = [];

    // Only process new notifications if not in delete-only mode
    if (!isDeleteOnly && Array.isArray(aList) && aList.length > 0) {
      for (const triggerTimestamp of aList) {
        const isTimestampToday = notificationTimezone
          ? isTodayInTimezone(triggerTimestamp, notificationTimezone)
          : isToday(triggerTimestamp);
        const matchName = data.matchName?.trim().split("(")[0].trim() || "";

        if (isTimestampToday) {
          if (gId.includes("contents")) {
            todayNotificationPromises.push(
              processClubAdminNotifications(data, ctnId, notificationTimezone),
            );
          } else {
            todayNotificationPromises.push(
              _sendNotification(
                data.title,
                data.type,
                data.volunteerRequests || {},
                data.ticketId,
                eventDate,
                matchName,
                gId,
                null,
                ctnId,
                data.showForFans,
              ),
            );
          }
        } else {
          let dayTimestamp = null;
          const verifiedTimestamp = verifyEventTimestamp(triggerTimestamp);
          if (verifiedTimestamp.isValid) {
            dayTimestamp = verifiedTimestamp.timestamp;
          } else {
            logger.error(verifiedTimestamp.errorMessage);
            continue;
          }
          if (dayTimestamp) {
            const notificationId = doc(
              collection(db, "contentNotifications"),
            ).id;
            const notificationRef = doc(
              db,
              "contentNotifications",
              gId,
              "dates",
              dayTimestamp.toString(),
              "notifications",
              notificationId,
            );

            const newNotification = {
              status: "pending",
              eventId: ctnId,
              eventTime: eventDate,
              triggerTime: triggerTimestamp,
              eventName: data.title || "",
              matchName: matchName,
              ticketId: data.ticketId,
              eventType: data.type,
              volunteerOption:
                Object.keys(data.volunteerRequests || {}).length > 0,
              addedOn: Date.now(),
              showForFans: data.showForFans || false,
            };

            newNotificationPromises.push(
              setDoc(notificationRef, newNotification, { merge: true }),
            );
          }
        }
      }
    }

    const deletePromises = [];

    if (Array.isArray(dList) && dList.length > 0) {
      for (const triggerTimestamp of dList) {
        const verifiedTimestamp = verifyEventTimestamp(triggerTimestamp);
        let dayTimestamp = null;
        if (verifiedTimestamp.isValid) {
          dayTimestamp = verifiedTimestamp.timestamp;
        } else {
          logger.error(verifiedTimestamp.errorMessage);
          continue;
        }
        if (dayTimestamp) {
          const notificationsCollectionRef = collection(
            db,
            "contentNotifications",
            gId,
            "dates",
            dayTimestamp.toString(),
            "notifications",
          );

          const q = query(
            notificationsCollectionRef,
            where("triggerTime", "==", triggerTimestamp),
            where("eventId", "==", ctnId),
          );

          deletePromises.push(
            getDocs(q).then((querySnapshot) => {
              const docDeletePromises = [];
              querySnapshot.forEach((document) => {
                docDeletePromises.push(deleteDoc(document.ref));
              });
              return Promise.all(docDeletePromises);
            }),
          );
        }
      }
    }

    // Wait for all operations to complete
    await Promise.all([
      ...newNotificationPromises,
      ...deletePromises,
      ...todayNotificationPromises,
      ...updatePromises,
    ]);

    return { success: true };
  } catch (error) {
    logger.error("Error updating notifications:", error);
    return { success: false, error };
  }
};

function haveEventDetailsChanged(newData, oldData) {
  if (!oldData) return true;

  return (
    newData?.title !== oldData?.title ||
    newData?.type !== oldData?.type ||
    newData?.details?.eventStartDate !== oldData?.details?.eventStartDate ||
    newData?.matchName !== oldData?.matchName ||
    newData?.ticketId !== oldData?.ticketId ||
    JSON.stringify(newData?.volunteerRequests) !==
      JSON.stringify(oldData?.volunteerRequests) ||
    newData?.showForFans !== oldData?.showForFans
  );
}
